/**
 * Stock Documents - UI แบบ foodorder เชื่อมระบบคลัง thunderbee
 * ใช้ getDataWithComp (API_1001_WHDOC_H, API_1002_WHDOC_D)
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Col,
  DatePicker,
  Input,
  Row,
  Space,
  Table,
} from "antd";
import { PlusOutlined, PrinterOutlined, HistoryOutlined, EditOutlined, EyeOutlined } from "@ant-design/icons";
import thTH from "antd/es/date-picker/locale/th_TH";
import dayjs from "dayjs";
import buddhistEra from "dayjs/plugin/buddhistEra";
import "dayjs/locale/th";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import DataTable from "../../../components/DataTable";
import FileModalWHDoc from "../../components/Modal/FileModalWHDoc";
import { getDataWithComp, getAlert } from "../../../utils/SamuiUtils";
import { getReport } from "../../../utils/ReportUtils";

dayjs.extend(buddhistEra);
dayjs.locale("th");

const toYMD = (d) => {
  if (!d) return null;
  const dt = d?.toDate ? d.toDate() : new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const formatDateBuddhist = (value) => {
  if (!value) return "";
  const buddhistYear = value.year() + 543;
  return `${value.format("DD-MM-")}${buddhistYear}`;
};

const datePickerLocaleBE = {
  ...thTH,
  lang: {
    ...thTH.lang,
    locale: "th",
    fieldDateFormat: "DD-MM-BBBB",
    fieldDateTimeFormat: "DD-MM-BBBB HH:mm:ss",
    yearFormat: "BBBB",
    cellYearFormat: "BBBB",
  },
};

// TR/CNT: DB เก็บเวลาไทยแล้ว ดึง literal ตรงๆ
const formatTH = (v) => {
  if (!v) return "-";
  const m = String(v).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return String(v);
  const [, yr, mo, dy, hh, mm, ss] = m;
  return `${dy}/${mo}/${Number(yr) + 543} ${hh}:${mm}:${ss}`;
};

// ADJ: DB เก็บ UTC แปลงเป็นเวลาไทย (+7)
const formatTH_UTC = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("th-TH", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// WHDoc_Type: 1=ปรับปรุง 2=โอนย้าย 3=นับสต๊อก
const DOC_TYPE_MAP = { ADJ: "1", TR: "2", CNT: "3" };
const DOC_PREFIX_MAP = { ADJ: "ADJ", TR: "TNS", CNT: "CNT" };
const DOC_LABEL_MAP = { ADJ: "ใบปรับปรุงสินค้า", TR: "ใบโอนย้าย", CNT: "ใบนับสต๊อก" };

const DOCS_PAGE_KEY = (tab) => `StockDocumentsPage_${tab}`;
const DOCS_SIZE_KEY = "StockDocumentsPageSize";
const DOCS_SEARCH_KEY = "StockDocumentsSearch";
const DOCS_DATE_FROM_KEY = "StockDocumentsDateFrom";
const DOCS_DATE_TO_KEY = "StockDocumentsDateTo";
const getDocsPage = (tab) => Number(localStorage.getItem(DOCS_PAGE_KEY(tab))) || 1;
const getDocsPageSize = () => Number(localStorage.getItem(DOCS_SIZE_KEY)) || 100;
const getDocsDateFrom = () => {
  const v = localStorage.getItem(DOCS_DATE_FROM_KEY);
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};
const getDocsDateTo = () => {
  const v = localStorage.getItem(DOCS_DATE_TO_KEY);
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

export default function StockDocumentsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isFromStockHistory = Boolean(location.state?.fromStockHistory && location.state?.backLink);
  const historyDocNo = location.state?.activeDocNo || location.state?.searchDoc || null;
  const restoreDocId = location.state?.selectedDocId ?? localStorage.getItem("StockDocumentsSelectedDoc") ?? null;
  const restoreTab = location.state?.docTypeFilter ?? null;
  const initSearch = location.state?.searchDoc ?? localStorage.getItem(DOCS_SEARCH_KEY) ?? "";
  const initActiveDocNo = location.state?.activeDocNo ?? null;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [q, setQ] = useState(initSearch);
  const [searchTerm, setSearchTerm] = useState(initSearch);
  const [activeDocNo, setActiveDocNo] = useState(initActiveDocNo);
  const [dateFrom, setDateFrom] = useState(() => getDocsDateFrom());
  const [dateTo, setDateTo] = useState(() => getDocsDateTo());
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docItems, setDocItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [maxHeight, setMaxHeight] = useState(400);
  const [showFileModal, setShowFileModal] = useState(false);
  const [docTypeFilter, setDocTypeFilter] = useState(
    restoreTab || localStorage.getItem("StockDocumentsTab") || "ADJ"
  );

  useEffect(() => { localStorage.setItem(DOCS_SEARCH_KEY, searchTerm); }, [searchTerm]);
  useEffect(() => {
    if (dateFrom) localStorage.setItem(DOCS_DATE_FROM_KEY, dateFrom.toISOString());
    else localStorage.removeItem(DOCS_DATE_FROM_KEY);
  }, [dateFrom]);
  useEffect(() => {
    if (dateTo) localStorage.setItem(DOCS_DATE_TO_KEY, dateTo.toISOString());
    else localStorage.removeItem(DOCS_DATE_TO_KEY);
  }, [dateTo]);

  const fetchDocs = async (override = {}) => {
    setLoadingDocs(true);
    try {
      const _searchTerm = String(override.searchTerm ?? searchTerm ?? "").trim();
      const escapedSearchTerm = _searchTerm.replace(/'/g, "''");
      const searchCondition = escapedSearchTerm
        ? ` AND (WHDoc_No LIKE '%${escapedSearchTerm}%' OR ISNULL(WHDoc_Remark,'') LIKE '%${escapedSearchTerm}%' OR ISNULL(WHDoc_CreatedBy,'') LIKE '%${escapedSearchTerm}%')`
        : "";
      const rows = await getDataWithComp(
        "WHDoc_H",
        `AND WHDoc_Type IN ('1','2','3')${searchCondition} ORDER BY WHDoc_No DESC`
      );
      let arr = Array.isArray(rows) ? rows : [];
      const _from = override.dateFrom ?? dateFrom;
      const _to = override.dateTo ?? dateTo;
      if (_from || _to) {
        const fromStr = _from ? toYMD(_from) : null;
        const toStr = _to ? toYMD(_to) : null;
        arr = arr.filter((d) => {
          const docDate = d.WHDoc_CreatedDate ? toYMD(new Date(d.WHDoc_CreatedDate)) : null;
          if (!docDate) return true;
          if (fromStr && docDate < fromStr) return false;
          if (toStr && docDate > toStr) return false;
          return true;
        });
      }
      setDocs(arr);
      if (arr.length > 0 && !override.selectedDoc) {
        const fromDocNo = initActiveDocNo ? arr.find((d) => (d.WHDoc_No ?? d.Doc_No) === initActiveDocNo) : null;
        const restored = fromDocNo ?? (restoreDocId ? arr.find((d) => String(d.WHDoc_Id) === String(restoreDocId)) : null);
        setSelectedDoc(restored ?? arr[0]);
        if (restored) {
          setTimeout(() => {
            const el = document.querySelector(".ant-table-row-selected");
            if (el) el.scrollIntoView({ block: "center", behavior: "instant" });
          }, 300);
        }
      }
    } catch (err) {
      getAlert("FAILED", err?.message || "โหลดรายการเอกสารไม่สำเร็จ");
      setDocs([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchDocItems = async (doc) => {
    if (!doc) return;
    setLoadingItems(true);
    try {
      const id = doc.WHDoc_Id ?? doc.whDocId ?? doc.Doc_Id ?? doc.id ?? null;
      if (!id) {
        setDocItems([]);
        return;
      }
      const rows = await getDataWithComp(
        "API_1002_WHDOC_D",
        `AND WHDoc_Id = ${id} ORDER BY Line ASC`
      );
      setDocItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      getAlert("FAILED", err?.message || "โหลดรายละเอียดเอกสารไม่สำเร็จ");
      setDocItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const goToStockForm = (route, tab) => {
    localStorage.setItem("TreasuryDocumentsOrigin", "/stock/stock-documents");
    localStorage.setItem("StockDocumentsTab", tab);
    navigate(route);
  };

  const handleSearch = () => {
    const nextSearchTerm = String(q || "").trim();
    setSearchTerm(nextSearchTerm);
    setSelectedDoc(null);
    localStorage.setItem(DOCS_PAGE_KEY(docTypeFilter), "1");
    fetchDocs({ searchTerm: nextSearchTerm, selectedDoc: null });
  };

  const filteredDocs = useMemo(() => {
    if (docTypeFilter === "ALL") return docs;
    const prefix = DOC_PREFIX_MAP[docTypeFilter];
    return docs.filter((d) => String(d.WHDoc_No ?? d.Doc_No ?? "").startsWith(prefix));
  }, [docs, docTypeFilter]);

  const docHeaderText = useMemo(() => {
    if (!selectedDoc) return "เลือกเอกสารทางซ้ายเพื่อดูรายละเอียด";
    const no = selectedDoc.WHDoc_No ?? selectedDoc.Doc_No ?? "-";
    const remark = selectedDoc.WHDoc_Remark ?? selectedDoc.Remark ?? "-";
    const date = formatTH_UTC(selectedDoc.WHDoc_CreatedDate ?? selectedDoc.CreatedAt) ?? null;
    return `เอกสารเลขที่: ${no} หมายเหตุ: ${remark}${date ? ` วันที่: ${date}` : ""}`;
  }, [selectedDoc]);

  const selectedDocType = useMemo(() => {
    if (!selectedDoc) return null;
    const t = String(selectedDoc.WHDoc_Type ?? "");
    if (t === "1" || t === "2" || t === "3") return t;
    const no = String(selectedDoc.WHDoc_No ?? selectedDoc.Doc_No ?? "");
    if (no.startsWith("ADJ")) return "1";
    if (no.startsWith("TNS")) return "2";
    if (no.startsWith("CNT")) return "3";
    return null;
  }, [selectedDoc]);

  const canPrintDoc = Boolean(selectedDoc && selectedDocType);

  const handlePrintDoc = async () => {
    if (!selectedDoc || !selectedDocType) return;
    const whDocId = selectedDoc?.WHDoc_Id ?? selectedDoc?.whDocId ?? selectedDoc?.Doc_Id ?? null;
    const whDocNo = selectedDoc?.WHDoc_No ?? selectedDoc?.Doc_No ?? null;
    await getReport("WHDOC", {
      WHDoc_Id: whDocId,
      WHDoc_No: whDocNo,
      WHDoc_Type: selectedDocType,
    });
  };

  useEffect(() => {
    fetchDocs();
    localStorage.removeItem("StockDocumentsSelectedDoc");
  }, []);

  useEffect(() => {
    if (!selectedDoc) {
      setDocItems([]);
      return;
    }
    fetchDocItems(selectedDoc);
  }, [selectedDoc]);

  useEffect(() => {
    if (filteredDocs.length === 0) {
      setSelectedDoc(null);
      return;
    }
    const stillInFilter = selectedDoc && filteredDocs.some(
      (d) => (d.WHDoc_Id ?? d.WHDoc_No) === (selectedDoc.WHDoc_Id ?? selectedDoc.WHDoc_No)
    );
    if (!stillInFilter) setSelectedDoc(filteredDocs[0]);
  }, [filteredDocs]);

  useEffect(() => {
    const update = () => setMaxHeight((window.innerHeight || 600) - 280);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const columnsDocs = useMemo(() => {
    const base = [
      { title: "ลำดับ", width: 60, align: "center", render: (_, r) => r.__globalIndex },
      { title: "เลขเอกสาร", width: 150, ellipsis: true, render: (_, r) => r.WHDoc_No ?? r.Doc_No ?? r.DOC_NO ?? "-" },
      { title: "วันที่สร้าง", width: 170, align: "center", render: (_, r) => formatTH_UTC(r.WHDoc_CreatedDate ?? r.CreatedAt) ?? "-" },
      { title: "หมายเหตุ", width: 220, ellipsis: true, render: (_, r) => r.WHDoc_Remark ?? r.Remark ?? "-" },
      {
        title: "ชื่อผู้สร้าง", width: 160,
        render: (_, r) => {
          const creator = r.WHDoc_CreatedBy ?? r.CreatedBy ?? "-";
          const updatedBy = r.WHDoc_UpdatedBy;
          const lineStyle = {
            color: "#6b7280",
            fontSize: 12,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          };
          return (
            <div>
              <div style={lineStyle}>สร้าง : {creator}</div>
              {updatedBy && (
                <div style={lineStyle}>นับ : {updatedBy}</div>
              )}
            </div>
          );
        },
      },
    ];
    if (docTypeFilter !== "CNT") return base;
    const statusCol = {
      title: "สถานะ",
      width: 130,
      align: "center",
      render: (_, r) => {
        const st = Number(r.WHDoc_Status);
        if (st === 14) return <span style={{ color: "#ca8a04" }}>รอบันทึกผล</span>;
        if (st === 1) return <span style={{ color: "#16a34a" }}>บันทึกผลแล้ว</span>;
        if (st === 13) return <span style={{ color: "#9ca3af" }}>ยกเลิก</span>;
        return <span>{r.WHDoc_Status ?? "-"}</span>;
      },
    };
    const editCol = {
      title: "",
      width: 50,
      align: "center",
      render: (_, r) => {
        const st = Number(r.WHDoc_Status);
        const id = r.WHDoc_Id ?? r.whDocId;
        if (id == null) return null;
        if (st === 14) {
          return (
            <button
              type="button"
              title="แก้ไข"
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem("StockDocumentsTab", "CNT");
                localStorage.setItem("StockDocumentsSelectedDoc", String(id));
                navigate(`/stock/stock-form-count?edit=${id}`, { state: { selectedDocId: id, docTypeFilter: "CNT" } });
              }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#E13F57", padding: 0, display: "inline-flex", alignItems: "center" }}
            >
              <EditOutlined style={{ fontSize: 16 }} />
            </button>
          );
        }
        if (st === 1) {
          return (
            <button
              type="button"
              title="ดูข้อมูล"
              onClick={(e) => {
                e.stopPropagation();
                localStorage.setItem("StockDocumentsTab", "CNT");
                localStorage.setItem("StockDocumentsSelectedDoc", String(id));
                navigate(`/stock/stock-form-count?view=${id}`, { state: { selectedDocId: id, docTypeFilter: "CNT" } });
              }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", padding: 0, display: "inline-flex", alignItems: "center" }}
            >
              <EyeOutlined style={{ fontSize: 16 }} />
            </button>
          );
        }
        return null;
      },
    };
    return [...base, statusCol, editCol];
  }, [docTypeFilter, navigate]);

  const historyBtn = {
    title: "", width: 40, align: "center", fixed: "right",
    render: (_, r) => {
      const itemId = r.Item_Id;
      const whId = r.F_WH_Id || r.T_WH_Id || r.WH_Id || selectedDoc?.F_WH_Id || selectedDoc?.T_WH_Id || selectedDoc?.WH_Id || null;
      const url = whId
        ? `/stock/stock-detail?tab=history&item_id=${itemId}&wh_id=${whId}`
        : `/stock/stock-detail?tab=history&item_id=${itemId}`;
      return (
        <button
          type="button"
          disabled={!itemId}
          onClick={() => navigate(url, { state: { from: "/stock/stock-documents", selectedDocId: selectedDoc?.WHDoc_Id, docTypeFilter } })}
          style={{ background: "none", border: "none", cursor: itemId ? "pointer" : "not-allowed", color: "#E13F57", opacity: itemId ? 1 : 0.3, padding: 0 }}
          title="ประวัติการเคลื่อนไหว"
        >
          <HistoryOutlined style={{ fontSize: 16 }} />
        </button>
      );
    },
  };

  const whCol = {
    title: "คลัง", ellipsis: true, width: 140,
    render: (_, r) => {
      const fWh = r.F_WH_Name ?? r.WH_Name ?? selectedDoc?.F_WH_Name ?? selectedDoc?.WH_Name;
      const tWh = r.T_WH_Name ?? selectedDoc?.T_WH_Name;
      const isTransfer = String(selectedDoc?.WHDoc_No ?? "").startsWith("TNS");
      if (isTransfer && fWh && tWh) return `${fWh} → ${tWh}`;
      return fWh ?? "-";
    },
  };

  const typeCol = {
    title: "ประเภท", width: 90, align: "center", render: (_, r) => {
      const t = r.Doc_Type;
      if (t === "AI") return <span style={{ color: "green" }}>ปรับเพิ่ม</span>;
      if (t === "AO") return <span style={{ color: "#E13F57" }}>ปรับลด</span>;
      if (t === "NC") return <span>เท่าเดิม</span>;
      return <span>{t ?? "-"}</span>;
    }
  };

  const columnsItems = [
    { title: "ลำดับ", width: 55, align: "center", render: (_, __, i) => i + 1 },
    { title: "ชื่อสินค้า", ellipsis: true, render: (_, r) => r.Item_Name ?? "-" },
    whCol,
    typeCol,
    { title: "จำนวน", width: 90, align: "right", render: (_, r) => Number(r.Item_Qty ?? 0).toLocaleString() },
    { title: "หน่วย", width: 80, align: "center", render: (_, r) => r.Item_Unit ?? "-" },
    historyBtn,
  ];

  const columnsItemsCnt = [
    { title: "ลำดับ", width: 55, align: "center", render: (_, __, i) => i + 1 },
    { title: "ชื่อสินค้า", width: 200, ellipsis: true, render: (_, r) => r.Item_Name ?? "-" },
    { ...whCol, width: 130 },
    { title: "ก่อนนับ", width: 100, align: "right", render: (_, r) => {
      const v = r.Item_Price_Unit ?? r.Before_Qty ?? r.Item_Before_Qty;
      return v != null ? Number(v).toLocaleString() : "-";
    }},
    { title: "นับได้", width: 90, align: "right", render: (_, r) => {
      const v = r.Item_Total;
      return v != null ? Number(v).toLocaleString() : "-";
    }},
    typeCol,
    { title: "จำนวนที่ปรับ", width: 110, align: "right", fixed: "right", render: (_, r) => {
      const v = r.Item_Qty;
      const unit = r.Item_Unit ?? "";
      return v != null ? `${Number(v).toLocaleString()} ${unit}`.trim() : "-";
    }},
    historyBtn,
  ];

  const breadcrumbs = isFromStockHistory
    ? [
        { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
        {
          label: "ดูสต๊อกสินค้า",
          link: location.state?.backState?.from || "/stock/stock-overview",
          state: location.state?.backState,
        },
        {
          label: "ประวัติการเคลื่อนไหวสินค้า",
          link: location.state?.backLink,
          state: location.state?.backState,
        },
        ...(historyDocNo ? [{ label: `เอกสาร ${historyDocNo}` }] : []),
      ]
    : [
        { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
        { label: "เอกสารงานคลัง" },
      ];

  const btnStyle = { background: "#E13F57", borderColor: "#E13F57" };

  return (
    <div className="WarehouseStock">
      <button
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="btn btn-toggle-sidebar"
        style={{ position: "fixed", top: "1rem", left: isSidebarOpen ? "190px" : "-15px", zIndex: 1001, background: "transparent", border: "none", color: "#E13F57", fontSize: "20px" }}
      >
        {isSidebarOpen ? "✖" : "☰"}
      </button>
      <div className="wrapper">
        <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
        <div
          className={`main-panel ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
          style={{ height: "100vh", overflow: "hidden" }}
        >
          <Topbar breadcrumbs={breadcrumbs} />
          <div className="container">
            <div className="page-inner" style={{ marginTop: "40px", height: "calc(100vh - 56px)", overflow: "hidden" }}>
              <div className="pt-3 pb-3 pl-2 pr-3" style={{ height: "100%" }}>
                <div className="bg-white rounded-lg shadow-sm px-4 py-4" style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
                  <Space direction="vertical" size={12} style={{ width: "100%", flex: 1, minHeight: 0 }}>
                    <Row justify="space-between" align="middle" className="mb-2">
                      <Col>
                        <div style={{ fontSize: 20, fontWeight: 600 }}>
                          เอกสารงานคลัง{DOC_LABEL_MAP[docTypeFilter] ? ` - ${DOC_LABEL_MAP[docTypeFilter]}` : ""}
                        </div>
                      </Col>
                      <Col>
                        <div style={{ fontSize: 14, color: "#6b7280" }}>{selectedDoc ? docHeaderText : ""}</div>
                      </Col>
                    </Row>

                    <Row gutter={[12, 12]} align="middle">
                      <Col xs={24} xl={24}>
                        <div className="flex flex-wrap items-center gap-2">
                          {[
                            { label: "ใบปรับปรุงสินค้า", type: "ADJ", path: "/stock/stock-form-adjust" },
                            { label: "ใบโอนย้าย", type: "TR", path: "/stock/stock-form-transfer" },
                            { label: "ใบนับสต๊อก", type: "CNT", path: "/stock/stock-form-count" },
                          ].map(({ label, type, path }) => {
                            const active = docTypeFilter === type;
                            const bg = active ? "#E13F57" : "#fff";
                            const base = { background: bg, borderColor: active ? "#E13F57" : "#f5c6cd", color: active ? "#fff" : "#333" };
                            return (
                              <div key={type} style={{ display: "inline-flex", borderRadius: 6, overflow: "hidden", border: `1px solid ${active ? "#E13F57" : "#f5c6cd"}` }}>
                                <button
                                  onClick={() => { localStorage.setItem(DOCS_PAGE_KEY(type), "1"); setDocTypeFilter(type); setSelectedDoc(null); }}
                                  className={active ? "" : "doc-type-btn-inactive"}
                                  style={{ ...base, border: "none", padding: "5px 12px", cursor: "pointer", fontSize: 14 }}
                                >
                                  {label}
                                </button>
                                <span style={{ background: bg, display: "flex", alignItems: "stretch", padding: "0", userSelect: "none" }}>
                                  <span style={{ width: 1, background: active ? "rgba(255,255,255,0.5)" : "#f5c6cd", display: "block" }} />
                                </span>
                                <button
                                  onClick={() => goToStockForm(path, type)}
                                  className={active ? "" : "doc-type-btn-inactive"}
                                  style={{ ...base, border: "none", padding: "5px 10px", cursor: "pointer", fontSize: 15, fontWeight: 700 }}
                                >
                                  <PlusOutlined />
                                </button>
                              </div>
                            );
                          })}
                          <Input
                            placeholder="Search"
                            value={q}
                            onChange={(e) => {
                              const nextValue = e.target.value;
                              setQ(nextValue);
                              if (nextValue === "") {
                                setSearchTerm("");
                                setSelectedDoc(null);
                                fetchDocs({ searchTerm: "", selectedDoc: null });
                              }
                            }}
                            onPressEnter={handleSearch}
                            allowClear
                            style={{ width: 220 }}
                          />
                          <span className="text-gray-500 text-sm">จากวันที่สร้าง</span>
                          <DatePicker
                            value={dateFrom}
                            onChange={setDateFrom}
                            format={formatDateBuddhist}
                            locale={datePickerLocaleBE}
                            inputReadOnly
                          />
                          <span className="text-gray-500 text-sm">ถึงวันที่สร้าง</span>
                          <DatePicker
                            value={dateTo}
                            onChange={setDateTo}
                            format={formatDateBuddhist}
                            locale={datePickerLocaleBE}
                            inputReadOnly
                          />
                          <Button onClick={handleSearch}>ค้นหา</Button>
                          <Button
                            disabled={!selectedDoc}
                            onClick={() => setShowFileModal(true)}
                            style={{ marginLeft: "auto", background: "rgb(239,108,0)", borderColor: "rgb(239,108,0)", color: "#fff" }}
                          >
                            <i className="fa fa-paperclip me-1"></i>แนบไฟล์
                          </Button>
                          <Button
                            icon={<PrinterOutlined />}
                            disabled={!canPrintDoc}
                            onClick={handlePrintDoc}
                          >
                            พิมพ์เอกสาร
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    <Row gutter={[24, 12]} style={{ flex: 1, minHeight: 0 }}>
                      <Col xs={24} lg={11} style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <DataTable
                          key={docTypeFilter}
                          columns={columnsDocs}
                          dataSource={filteredDocs}
                          loading={loadingDocs}
                          rowKey={(r) => String(r.WHDoc_Id ?? r.WHDoc_No ?? r.Doc_No ?? Math.random())}
                          defaultPageSize={100}
                          pageSizeOptions={[20, 50, 100]}
                          tableHeight={Math.max(320, maxHeight - 120)}
                          initialPage={getDocsPage(docTypeFilter)}
                          initialPageSize={getDocsPageSize()}
                          onPageChange={(page) => localStorage.setItem(DOCS_PAGE_KEY(docTypeFilter), String(page))}
                          onPageSizeChange={(size) => {
                            localStorage.setItem(DOCS_SIZE_KEY, String(size));
                            localStorage.setItem(DOCS_PAGE_KEY(docTypeFilter), "1");
                          }}
                          onRow={(record) => ({ onClick: () => setSelectedDoc(record) })}
                          rowClassName={(record) =>
                            (record.WHDoc_Id ?? record.WHDoc_No) === (selectedDoc?.WHDoc_Id ?? selectedDoc?.WHDoc_No)
                              ? "ant-table-row-selected"
                              : ""
                          }
                        />
                      </Col>
                      <Col xs={24} lg={13} style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
                        <div className="data-table-wrapper">
                          <div className="data-table-root" style={{ height: Math.max(320, maxHeight - 120) + 55 }}>
                            <Table
                              size="middle"
                              columns={docTypeFilter === "CNT" ? columnsItemsCnt : columnsItems}
                              dataSource={docItems}
                              loading={loadingItems}
                              rowKey={(r) => String(r.DT_Id ?? r.Line ?? Math.random())}
                              pagination={false}
                              scroll={docTypeFilter === "CNT"
                                ? { y: Math.max(320, maxHeight - 120), x: "max-content" }
                                : { y: Math.max(320, maxHeight - 120) }
                              }
                            />
                          </div>
                          <div className="data-table-footer" style={{ minHeight: 53 }}>
                            <span style={{ fontSize: 14 }}>จำนวนรายการ : <b>{docItems.length}</b> รายการ</span>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Space>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <FileModalWHDoc
        showModal={showFileModal}
        setShowModal={setShowFileModal}
        WHDoc_Id={selectedDoc?.WHDoc_Id ?? selectedDoc?.whDocId ?? null}
        WHDoc_No={selectedDoc?.WHDoc_No ?? selectedDoc?.Doc_No ?? null}
      />
    </div>
  );
}
